// AI 답변 텍스트 렌더러 — 마크다운(제목·목록·표·코드블록·인용·구분선·인라인 서식) + 출처 배지 + URL 도메인 칩
//
// 문단 간격은 이 컴포넌트가 CSS로 정한다. 부모에 white-space: pre-wrap을 걸면
// 원문의 빈 줄이 그대로 살아나 마진과 겹치므로, 답변마다 간격이 달라진다.
import React, { Fragment } from 'react';
import ChartBlock, { ChartSkeleton } from './Chart/ChartBlock';
import styles from './RichText.module.css';

/* 인라인 서식 — 출처 배지 | **굵게** | *기울임* | `코드` | [텍스트](URL) | 맨몸 URL
   출처는 대괄호 안의 내용만 캡처 — "출처:" 접두어는 칩 아이콘이 대신한다 */
const INLINE_RE = /\[(?:출처|出典)\s*:?\s*([^\]]+)\]|\*\*([^*]+)\*\*|\*([^*\n]+)\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s<>"')\]]+)/g;

/* 웹 출처 — 도메인명 칩. 원문 URL은 title·href로만 유지 */
function UrlChip({ url, label }: { url: string; label?: string }) {
  let host = url;
  try { host = new URL(url).hostname.replace(/^www\./, ''); } catch { /* 파싱 실패 시 원문 표시 */ }
  return (
    <a href={url} target="_blank" rel="noreferrer" className={styles.urlChip} title={url}>
      <svg className={styles.urlChipIcon} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
      <span className={styles.urlChipHost}>{label || host}</span>
    </a>
  );
}

/* 사내 문서 출처 — 웹 출처(UrlChip)와 같은 칩 형태, 아이콘만 문서로 구분 */
function DocSourceChip({ label }: { label: string }) {
  return (
    <span className={`${styles.urlChip} ${styles.docChip}`} title={`출처: ${label}`}>
      <svg className={styles.urlChipIcon} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" />
      </svg>
      <span className={styles.urlChipHost}>{label}</span>
    </span>
  );
}

function renderInline(text: string) {
  const parts: (string | JSX.Element)[] = [];
  const re = new RegExp(INLINE_RE);
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const key = i++;
    const [full, docSource, bold, italic, code, linkText, linkUrl, bareUrl] = match;
    if (docSource !== undefined) {
      parts.push(<DocSourceChip key={key} label={docSource.trim()} />);
    } else if (bold !== undefined) {
      parts.push(<strong key={key}>{bold}</strong>);
    } else if (italic !== undefined) {
      parts.push(<em key={key}>{italic}</em>);
    } else if (code !== undefined) {
      parts.push(<code key={key} className={styles.inlineCode}>{code}</code>);
    } else if (linkText !== undefined) {
      parts.push(<UrlChip key={key} url={linkUrl} label={linkText} />);
    } else {
      /* 맨몸 URL — 문장 끝 구두점은 칩 밖으로 */
      const trail = bareUrl.match(/[.,;:!?]+$/)?.[0] ?? '';
      const clean = trail ? bareUrl.slice(0, -trail.length) : bareUrl;
      parts.push(<UrlChip key={key} url={clean} />);
      if (trail) parts.push(trail);
    }
    lastIndex = match.index + full.length;
  }
  parts.push(text.slice(lastIndex));
  return parts;
}

/* ── 블록 파싱 ── */
const TABLE_ROW_RE = /^\s*\|.*\|\s*$/;
const TABLE_SEP_RE = /^\s*\|(\s*:?-{2,}:?\s*\|)+\s*$/;
const HEADING_RE = /^\s{0,3}(#{1,4})\s+(.+?)\s*#*\s*$/;
const HR_RE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;
const FENCE_RE = /^\s*```/;
/* 펜스의 언어 태그 — ```chart 는 코드가 아니라 차트로 렌더한다 */
const FENCE_LANG_RE = /^\s*```\s*([A-Za-z0-9_-]*)/;
/* 들여쓰기를 캡처한다 — 중첩 목록의 깊이 판정에 쓴다 */
const UL_RE = /^(\s*)[-*•]\s+(.+)$/;
const OL_RE = /^(\s*)(\d+)[.)]\s+(.+)$/;
const QUOTE_RE = /^\s*>\s?(.*)$/;

interface ListItem {
  indent: number;
  ordered: boolean;
  text: string;
}

type Block =
  | { type: 'text'; content: string }
  | { type: 'table'; header: string[]; rows: string[][] }
  | { type: 'heading'; level: number; content: string }
  | { type: 'list'; start: number; items: ListItem[] }
  | { type: 'code'; content: string }
  | { type: 'chart'; content: string }
  | { type: 'chartPending' }
  | { type: 'quote'; content: string }
  | { type: 'hr' };

function splitCells(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map(c => c.trim());
}

function parseBlocks(text: string): Block[] {
  const lines = text.split('\n');
  const blocks: Block[] = [];
  let textBuf: string[] = [];

  const flushText = () => {
    if (textBuf.length > 0) {
      blocks.push({ type: 'text', content: textBuf.join('\n') });
      textBuf = [];
    }
  };
  /* 블록 뒤 공백 줄 하나는 소비 — pre-wrap 환경에서 블록 아래 불필요한 빈 줄 방지 */
  const skipBlank = (idx: number) => (idx < lines.length && lines[idx].trim() === '' ? idx + 1 : idx);

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    /* 표 */
    if (TABLE_ROW_RE.test(line) && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1])) {
      flushText();
      const header = splitCells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && TABLE_ROW_RE.test(lines[i]) && !TABLE_SEP_RE.test(lines[i])) {
        rows.push(splitCells(lines[i]));
        i++;
      }
      blocks.push({ type: 'table', header, rows });
      i = skipBlank(i);
      continue;
    }

    /* 코드블록 — 스트리밍 중 닫는 펜스 미도착 시 끝까지 코드로 취급 */
    if (FENCE_RE.test(line)) {
      flushText();
      const lang = line.match(FENCE_LANG_RE)?.[1]?.toLowerCase() ?? '';
      const buf: string[] = [];
      i++;
      while (i < lines.length && !FENCE_RE.test(lines[i])) {
        buf.push(lines[i]);
        i++;
      }
      const closed = i < lines.length;
      if (closed) i++; // 닫는 펜스 소비
      // 차트는 JSON이 다 도착해야 파싱된다. 도착 전까지 코드블록으로 두면 원문 JSON이
      // 그대로 노출되므로, 완결 전에는 자리만 잡아둔다.
      if (lang === 'chart') {
        blocks.push(closed
          ? { type: 'chart', content: buf.join('\n') }
          : { type: 'chartPending' });
      }
      else {
        blocks.push({ type: 'code', content: buf.join('\n') });
      }
      i = skipBlank(i);
      continue;
    }

    /* 제목 */
    const h = line.match(HEADING_RE);
    if (h) {
      flushText();
      blocks.push({ type: 'heading', level: h[1].length, content: h[2] });
      i = skipBlank(i + 1);
      continue;
    }

    /* 구분선 */
    if (HR_RE.test(line)) {
      flushText();
      blocks.push({ type: 'hr' });
      i = skipBlank(i + 1);
      continue;
    }

    /* 목록 (연속 줄 묶음) — 번호·불릿이 섞여도 한 블록으로 모아 중첩 구조로 복원한다 */
    const ul = line.match(UL_RE);
    const ol = line.match(OL_RE);
    if (ul || ol) {
      flushText();
      const start = ol ? parseInt(ol[2], 10) : 1;
      const items: ListItem[] = [];
      while (i < lines.length) {
        const u = lines[i].match(UL_RE);
        const o = lines[i].match(OL_RE);
        if (!u && !o) break;
        if (o) items.push({ indent: o[1].length, ordered: true, text: o[3] });
        else items.push({ indent: u![1].length, ordered: false, text: u![2] });
        i++;
      }
      blocks.push({ type: 'list', start, items });
      i = skipBlank(i);
      continue;
    }

    /* 인용 (연속 줄 묶음) */
    if (QUOTE_RE.test(line)) {
      flushText();
      const buf: string[] = [];
      while (i < lines.length && QUOTE_RE.test(lines[i])) {
        buf.push(lines[i].match(QUOTE_RE)![1]);
        i++;
      }
      blocks.push({ type: 'quote', content: buf.join('\n') });
      i = skipBlank(i);
      continue;
    }

    textBuf.push(line);
    i++;
  }
  flushText();
  return blocks;
}

const HEADING_CLS = [styles.mdH1, styles.mdH2, styles.mdH3, styles.mdH4];

interface ListNode {
  text: string;
  ordered: boolean;
  children: ListNode[];
}

/**
 * 들여쓴 목록을 중첩 구조로 복원한다.
 * 들여쓰기 폭은 모델마다 2칸·4칸으로 제각각이라 절대값으로 나누지 않고,
 * 이 블록에 실제로 등장한 들여쓰기 값을 정렬해 그 순서를 깊이로 삼는다.
 */
function buildListTree(items: ListItem[]): ListNode[] {
  // Array.from — tsconfig가 target es5 / downlevelIteration 미사용이라 Set 스프레드가 컴파일되지 않는다
  const levels = Array.from(new Set(items.map(i => i.indent))).sort((a, b) => a - b);
  const roots: ListNode[] = [];
  const stack: ListNode[] = [];

  for (const item of items) {
    const depth = Math.min(levels.indexOf(item.indent), stack.length);
    const node: ListNode = { text: item.text, ordered: item.ordered, children: [] };
    if (depth === 0) roots.push(node);
    else stack[depth - 1].children.push(node);
    stack.length = depth;
    stack.push(node);
  }
  return roots;
}

/* 형제 그룹의 첫 항목으로 ol/ul을 정한다 — 한 단계 안에서 번호와 불릿이 섞이는 일은 드물다 */
function ListLevel({ nodes, start }: { nodes: ListNode[]; start?: number }) {
  const ordered = nodes[0]?.ordered ?? false;
  const children = nodes.map((node, i) => (
    <li key={i}>
      {renderInline(node.text)}
      {node.children.length > 0 && <ListLevel nodes={node.children} />}
    </li>
  ));
  return ordered ? (
    <ol className={styles.mdList} start={start}>{children}</ol>
  ) : (
    <ul className={styles.mdList}>{children}</ul>
  );
}

/* 텍스트 블록 — 빈 줄로 문단을 가른다.
   간격은 CSS가 정한다: 모델이 빈 줄을 하나 쓰든 셋을 쓰든 결과가 같아야 한다.
   문단 안의 단일 개행은 의도적 줄바꿈으로 보고 <br>로 살린다. */
function Paragraphs({ content }: { content: string }) {
  const paragraphs = content.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
  return (
    <>
      {paragraphs.map((para, pi) => (
        <p key={pi} className={styles.mdParagraph}>
          {para.split('\n').map((line, li) => (
            <Fragment key={li}>
              {li > 0 && <br />}
              {renderInline(line)}
            </Fragment>
          ))}
        </p>
      ))}
    </>
  );
}

export default function RichText({ text }: { text: string }) {
  const blocks = parseBlocks(text);
  return (
    <>
      {blocks.map((block, bi) => {
        switch (block.type) {
          case 'table':
            return (
              <table key={bi} className={styles.mdTable}>
                <thead>
                  <tr>{block.header.map((hd, hi) => <th key={hi}>{renderInline(hd)}</th>)}</tr>
                </thead>
                <tbody>
                  {block.rows.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => <td key={ci}>{renderInline(cell)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          case 'heading':
            return <div key={bi} className={`${styles.mdHeading} ${HEADING_CLS[block.level - 1]}`}>{renderInline(block.content)}</div>;
          case 'list':
            return <ListLevel key={bi} nodes={buildListTree(block.items)} start={block.start} />;
          case 'code':
            return <pre key={bi} className={styles.codeBlock}>{block.content}</pre>;
          case 'chart':
            return <ChartBlock key={bi} source={block.content} />;
          case 'chartPending':
            return <ChartSkeleton key={bi} />;
          case 'quote':
            return <blockquote key={bi} className={styles.mdQuote}>{renderInline(block.content)}</blockquote>;
          case 'hr':
            return <hr key={bi} className={styles.mdHr} />;
          default:
            return <Paragraphs key={bi} content={block.content} />;
        }
      })}
    </>
  );
}
